/**
 * Asset analysis round endpoints — AIMS backend.
 * Source: /api/v1/aims/rounds/*
 *
 * Rounds are materialised server-side when a contract↔asset link is
 * created (POST /contract-assets/). The list endpoint is paginated
 * and not asset-scoped — the FE filters by asset id locally.
 *
 * The backend exposes only:
 *   - GET    /api/v1/aims/rounds/
 *   - GET    /api/v1/aims/rounds/{id}/
 *   - PUT    /api/v1/aims/rounds/{id}/
 *   - PATCH  /api/v1/aims/rounds/{id}/
 *
 * There is NO POST or DELETE endpoint, so "Add new round" / "Delete"
 * actions must remain UI-only no-ops until the backend exposes them.
 */

import { aimsBackendApi } from "./api"
import { AssetAnalysisRound, PaginatedAssetAnalysisRoundList, PatchedAssetAnalysisRound } from "./types"

export type RoundListQuery = {
  page?: number
  page_size?: number
  asset?: number
  contract_type?: string
  status?: number
  analysis_status?: "complete" | "incomplete"
  subcategory?: number
  search?: string
}

export const aimsRoundsApi = aimsBackendApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * GET /api/v1/aims/rounds/
     * Paginated list of rounds.
     */
    listRounds: builder.query<PaginatedAssetAnalysisRoundList, RoundListQuery | void>({
      query: params => {
        const searchParams = new URLSearchParams()
        if (params?.page) searchParams.set("page", String(params.page))
        if (params?.page_size) searchParams.set("page_size", String(params.page_size))
        if (params?.asset) searchParams.set("asset", String(params.asset))
        if (params?.contract_type) searchParams.set("contract_type", params.contract_type)
        if (params?.status) searchParams.set("status", String(params.status))
        if (params?.analysis_status) searchParams.set("analysis_status", params.analysis_status)
        if (params?.subcategory) searchParams.set("subcategory", String(params.subcategory))
        if (params?.search) searchParams.set("search", params.search)

        return {
          url: "api/v1/aims/rounds/",
          params: searchParams,
        }
      },
      providesTags: result => [
        { type: "AimsRoundList" as const, id: "LIST" },
        ...(result?.results ?? []).map(r => ({ type: "AimsRound" as const, id: r.id })),
      ],
    }),

    /**
     * GET /api/v1/aims/rounds/{id}/
     */
    getRound: builder.query<AssetAnalysisRound, number>({
      query: id => ({
        url: `api/v1/aims/rounds/${id}/`,
      }),
      providesTags: (_r, _e, id) => [{ type: "AimsRound" as const, id }],
    }),

    /**
     * PUT /api/v1/aims/rounds/{id}/
     * Full replacement — every writable field required.
     */
    updateRound: builder.mutation<AssetAnalysisRound, { id: number; body: AssetAnalysisRound }>({
      query: ({ id, body }) => ({
        url: `api/v1/aims/rounds/${id}/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsRound" as const, id },
        { type: "AimsRoundList" as const, id: "LIST" },
      ],
    }),

    /**
     * PATCH /api/v1/aims/rounds/{id}/
     * Partial update — only the writable fields the user touched.
     */
    patchRound: builder.mutation<AssetAnalysisRound, { id: number; body: PatchedAssetAnalysisRound }>({
      query: ({ id, body }) => ({
        url: `api/v1/aims/rounds/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "AimsRound" as const, id },
        { type: "AimsRoundList" as const, id: "LIST" },
      ],
    }),
  }),
})

export const {
  useListRoundsQuery,
  useLazyListRoundsQuery,
  useGetRoundQuery,
  useUpdateRoundMutation,
  usePatchRoundMutation,
} = aimsRoundsApi
